import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useCanvaZoomWithAction, useDeselectItemAction, useDrawItemsWithActions, useSelectedItemWithActions } from "../../context";
import { DrawTypeEnum, } from "../../types";
import './Canva.scss';
import { DrawResizeButtonPosition, DrawSelectedLinePosition, getScaledStyle, getStylesDrawResizeButton, getStylesDrawRotateButton, getStylesForDrawSelectedLine } from "./utils";
import { DrawComponent } from "./components/DrawComponent";
import { SelectedItemActionsEnum } from "../../reducers/selectedItemReducer";
import { IconRotateClockwise } from "@tabler/icons";
export const Canva = function Canva({ canvaSize, isServer }) {
    const { drawItems, deleteItem } = useDrawItemsWithActions();
    const { canvaZoom, changeCanvaZoom } = useCanvaZoomWithAction();
    const deselectItemAction = useDeselectItemAction();
    const { selectedItem, isOnFocus, selectedItemAction, selectedItemActionStartedMousePosition, selectedItemActionStartedRect, moveSelectedItem, modifyItemProperty, resizeSelectedItem, rotateSelectedItem, stopSelectedItemAction, startItemResizeAction, startItemRotationAction } = useSelectedItemWithActions();
    const elementRef = useRef(null);
    //on first mount calculate the ideal canva scale for client browser window size.
    useLayoutEffect(() => {
        if (!isServer && elementRef.current !== null) {
            //@ts-ignore
            let scaleWidth = elementRef.current.clientWidth / canvaSize.width;
            //@ts-ignore
            let scaleHeight = elementRef.current.clientHeight / canvaSize.height;
            let idealScale = parseFloat(((scaleWidth < scaleHeight ? scaleWidth : scaleHeight) - 0.1).toFixed(1));
            idealScale < 0.2 && (idealScale = 0.2);
            idealScale > 2 && (idealScale = 2);
            changeCanvaZoom(idealScale);
        }
    }, [elementRef.current]);
    function onKeyDown(e) {
        if (selectedItem && !isServer) {
            // TODO solve random deletes.
            if (!isOnFocus && [46, 8].includes(e.keyCode) && selectedItem) {
                // deleteItem(selectedItem.id);
            }
            if (e.keyCode === 27) {
                deselectItemAction();
            }
            if (!isOnFocus && e.keyCode === 37) {
                modifyItemProperty(selectedItem.id, 'position', { x: selectedItem.position.x - 1, y: selectedItem.position.y });
            }
            if (!isOnFocus && e.keyCode === 39) {
                modifyItemProperty(selectedItem.id, 'position', { x: selectedItem.position.x + 1, y: selectedItem.position.y });
            }
            if (!isOnFocus && e.keyCode === 38) {
                modifyItemProperty(selectedItem.id, 'position', { x: selectedItem.position.x, y: selectedItem.position.y - 1 });
            }
            if (!isOnFocus && e.keyCode === 40) {
                modifyItemProperty(selectedItem.id, 'position', { x: selectedItem.position.x, y: selectedItem.position.y + 1 });
            }
        }
    }
    useEffect(function () {
        document.addEventListener('keydown', onKeyDown);
        return function () {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [selectedItem, isOnFocus, deleteItem, modifyItemProperty]);
    let canvaStyles = getScaledStyle({ overflow: 'hidden', position: 'relative' }, canvaSize, canvaZoom);
    const startResize = useCallback(function (e, type) {
        e.stopPropagation();
        if (selectedItem) {
            startItemResizeAction(selectedItem.id, type, { x: e.clientX, y: e.clientY }, { ...selectedItem.position, ...selectedItem.size });
        }
    }, [startItemResizeAction, selectedItem]);
    const startRotation = useCallback(function (e) {
        e.stopPropagation();
        if (selectedItem) {
            // @ts-ignore
            let parentPosition = e.currentTarget.parentElement.getBoundingClientRect();
            startItemRotationAction(selectedItem.id, { x: parentPosition.x + parentPosition.width / 2, y: parentPosition.y + parentPosition.height / 2 });
        }
    }, [startItemRotationAction, selectedItem]);
    return _jsx("div", { className: 'editor-component-canva-root', ref: elementRef, onMouseDown: deselectItemAction, onMouseMove: (e) => {
            if (selectedItemAction !== SelectedItemActionsEnum.Idle && e.buttons === 0) {
                stopSelectedItemAction();
            }
            else if (selectedItem) {
                switch (selectedItemAction) {
                    case SelectedItemActionsEnum.ItemMove:
                        moveSelectedItem({ x: selectedItemActionStartedRect.x + (e.clientX - selectedItemActionStartedMousePosition.x) / canvaZoom, y: selectedItemActionStartedRect.y + (e.clientY - selectedItemActionStartedMousePosition.y) / canvaZoom });
                        break;
                    case SelectedItemActionsEnum.ResizeLeftTop:
                    case SelectedItemActionsEnum.ResizeTop:
                    case SelectedItemActionsEnum.ResizeRightTop:
                    case SelectedItemActionsEnum.ResizeRight:
                    case SelectedItemActionsEnum.ResizeRightBottom:
                    case SelectedItemActionsEnum.ResizeBottom:
                    case SelectedItemActionsEnum.ResizeLeftBottom:
                    case SelectedItemActionsEnum.ResizeLeft:
                        if (selectedItem.type === DrawTypeEnum.Circle) {
                            resizeSelectedItem(calcNewSizeForResizeAndKeepWHRatio(e, selectedItemActionStartedMousePosition, selectedItemActionStartedRect, canvaZoom, selectedItemAction));
                        }
                        else {
                            resizeSelectedItem(calcNewSizeForResize(e, selectedItemActionStartedMousePosition, selectedItemActionStartedRect, canvaZoom, selectedItemAction));
                        }
                        break;
                    case SelectedItemActionsEnum.Rotate:
                        rotateSelectedItem(calcNewRotationAngle(e, selectedItemActionStartedMousePosition));
                        break;
                }
            }
        }, children: _jsx("div", { className: 'editor-component-canva-centered-container-parent-parent', children: _jsxs("div", { className: 'editor-component-canva-centered-container-parent', children: [_jsx("div", { className: 'editor-component-canva-centered-container', children: _jsx("div", { style: canvaStyles, children: drawItems.map(d => _jsx(DrawComponent, { componentData: d, isServer: isServer }, d.id)) }) }), !isServer && selectedItem && _jsxs("div", { className: 'editor-component-canva-draw-selected-container', style: {
                            left: `${(selectedItem.position.x + selectedItem.size.width / 2 + 2) * canvaZoom}px`,
                            top: `${(selectedItem.position.y + selectedItem.size.height / 2 + 2) * canvaZoom}px`,
                            position: 'absolute',
                            transform: `rotate(${selectedItem.rotationAngle}rad)`
                        }, children: [_jsx("div", { className: 'editor-component-canva-draw-selected-line', style: getStylesForDrawSelectedLine(selectedItem.position, selectedItem.size, DrawSelectedLinePosition.Top, canvaZoom) }), _jsx("div", { className: 'editor-component-canva-draw-selected-line', style: getStylesForDrawSelectedLine(selectedItem.position, selectedItem.size, DrawSelectedLinePosition.Right, canvaZoom) }), _jsx("div", { className: 'editor-component-canva-draw-selected-line', style: getStylesForDrawSelectedLine(selectedItem.position, selectedItem.size, DrawSelectedLinePosition.Bottom, canvaZoom) }), _jsx("div", { className: 'editor-component-canva-draw-selected-line', style: getStylesForDrawSelectedLine(selectedItem.position, selectedItem.size, DrawSelectedLinePosition.Left, canvaZoom) }), _jsx("div", { className: 'editor-component-canva-resize-button', style: getStylesDrawResizeButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.LeftTop, canvaZoom), onMouseDown: (e) => { startResize(e, SelectedItemActionsEnum.ResizeLeftTop); } }), _jsx("div", { className: 'editor-component-canva-resize-button', style: getStylesDrawResizeButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.Top, canvaZoom), onMouseDown: (e) => { startResize(e, SelectedItemActionsEnum.ResizeTop); } }), _jsx("div", { className: 'editor-component-canva-resize-button', style: getStylesDrawResizeButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.RightTop, canvaZoom), onMouseDown: (e) => { startResize(e, SelectedItemActionsEnum.ResizeRightTop); } }), _jsx("div", { className: 'editor-component-canva-resize-button', style: getStylesDrawResizeButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.Right, canvaZoom), onMouseDown: (e) => { startResize(e, SelectedItemActionsEnum.ResizeRight); } }), _jsx("div", { className: 'editor-component-canva-resize-button', style: getStylesDrawResizeButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.RightBottom, canvaZoom), onMouseDown: (e) => { startResize(e, SelectedItemActionsEnum.ResizeRightBottom); } }), _jsx("div", { className: 'editor-component-canva-resize-button', style: getStylesDrawResizeButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.Bottom, canvaZoom), onMouseDown: (e) => { startResize(e, SelectedItemActionsEnum.ResizeBottom); } }), _jsx("div", { className: 'editor-component-canva-resize-button', style: getStylesDrawResizeButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.LeftBottom, canvaZoom), onMouseDown: (e) => { startResize(e, SelectedItemActionsEnum.ResizeLeftBottom); } }), _jsx("div", { className: 'editor-component-canva-resize-button', style: getStylesDrawResizeButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.Left, canvaZoom), onMouseDown: (e) => { startResize(e, SelectedItemActionsEnum.ResizeLeft); } }), _jsx("div", { className: 'editor-component-canva-rotate-button', style: getStylesDrawRotateButton(selectedItem.position, selectedItem.size, DrawResizeButtonPosition.Left, canvaZoom), onMouseDown: (e) => { startRotation(e); }, children: _jsx(IconRotateClockwise, { size: 16 }) })] })] }) }) });
};
function calcNewRotationAngle(e, start) {
    let vector1 = { x: e.pageX - start.x, y: e.pageY - start.y };
    return -Math.PI / 2 + Math.atan2(vector1.y, vector1.x); //radians
}
function calcNewSizeForResize(e, mouseStartResizeCoords, startRect, canvaZoom, resizeType) {
    let rect = { ...startRect };
    if ([SelectedItemActionsEnum.ResizeLeftTop, SelectedItemActionsEnum.ResizeLeft, SelectedItemActionsEnum.ResizeLeftBottom].includes(resizeType)) {
        rect.x = startRect.x + (e.clientX - mouseStartResizeCoords.x) / canvaZoom;
        rect.width = startRect.width - (e.clientX - mouseStartResizeCoords.x) / canvaZoom;
        if (rect.x > startRect.x + startRect.width - 1)
            rect.x = startRect.x + startRect.width - 1;
        if (rect.width < 1)
            rect.width = 1;
    }
    if ([SelectedItemActionsEnum.ResizeLeftTop, SelectedItemActionsEnum.ResizeTop, SelectedItemActionsEnum.ResizeRightTop].includes(resizeType)) {
        rect.y = startRect.y + (e.clientY - mouseStartResizeCoords.y) / canvaZoom;
        rect.height = startRect.height - (e.clientY - mouseStartResizeCoords.y) / canvaZoom;
        if (rect.y > startRect.y + startRect.height - 1)
            rect.y = startRect.y + startRect.height - 1;
        if (rect.height < 1)
            rect.height = 1;
    }
    if ([SelectedItemActionsEnum.ResizeRightTop, SelectedItemActionsEnum.ResizeRight, SelectedItemActionsEnum.ResizeRightBottom].includes(resizeType)) {
        rect.width = startRect.width + (e.clientX - mouseStartResizeCoords.x) / canvaZoom;
        if (rect.width < 1)
            rect.width = 1;
    }
    if ([SelectedItemActionsEnum.ResizeRightBottom, SelectedItemActionsEnum.ResizeBottom, SelectedItemActionsEnum.ResizeLeftBottom].includes(resizeType)) {
        rect.height = startRect.height + (e.clientY - mouseStartResizeCoords.y) / canvaZoom;
        if (rect.height < 1)
            rect.height = 1;
    }
    return rect;
}
function calcNewSizeForResizeAndKeepWHRatio(e, mouseStartResizeCoords, startRect, canvaZoom, resizeType) {
    let rect = { ...startRect };
    if ([SelectedItemActionsEnum.ResizeLeftTop].includes(resizeType)) {
        let minMove = Math.max(e.clientY - mouseStartResizeCoords.y, e.clientX - mouseStartResizeCoords.x);
        rect.x = startRect.x + minMove / canvaZoom;
        rect.width = startRect.width - (minMove) / canvaZoom;
        if (rect.x > startRect.x + startRect.width - 2)
            rect.x = startRect.x + startRect.width - 2;
        if (rect.width < 2)
            rect.width = 2;
        rect.y = startRect.y + minMove / canvaZoom;
        rect.height = startRect.height - minMove / canvaZoom;
        if (rect.y > startRect.y + startRect.height - 2)
            rect.y = startRect.y + startRect.height - 2;
        if (rect.height < 2)
            rect.height = 2;
    }
    if ([SelectedItemActionsEnum.ResizeRightTop].includes(resizeType)) {
        let minMove = Math.max(mouseStartResizeCoords.y - e.clientY, e.clientX - mouseStartResizeCoords.x);
        rect.width = startRect.width + (minMove) / canvaZoom;
        if (rect.width < 2)
            rect.width = 2;
        rect.y = startRect.y - minMove / canvaZoom;
        rect.height = startRect.height + minMove / canvaZoom;
        if (rect.y > startRect.y + startRect.height - 2)
            rect.y = startRect.y + startRect.height - 2;
        if (rect.height < 2)
            rect.height = 2;
    }
    if ([SelectedItemActionsEnum.ResizeLeftBottom].includes(resizeType)) {
        let minMove = Math.max(e.clientY - mouseStartResizeCoords.y, mouseStartResizeCoords.x - e.clientX);
        rect.x = startRect.x - minMove / canvaZoom;
        rect.width = startRect.width + (minMove) / canvaZoom;
        if (rect.x > startRect.x + startRect.width - 2)
            rect.x = startRect.x + startRect.width - 2;
        if (rect.width < 2)
            rect.width = 2;
        rect.height = startRect.height + minMove / canvaZoom;
        if (rect.y > startRect.y + startRect.height - 2)
            rect.y = startRect.y + startRect.height - 2;
        if (rect.height < 2)
            rect.height = 2;
    }
    if ([SelectedItemActionsEnum.ResizeRightBottom].includes(resizeType)) {
        let minMove = Math.max(e.clientY - mouseStartResizeCoords.y, e.clientX - mouseStartResizeCoords.x);
        rect.width = startRect.width + (minMove) / canvaZoom;
        if (rect.x > startRect.x + startRect.width - 2)
            rect.x = startRect.x + startRect.width - 2;
        if (rect.width < 2)
            rect.width = 2;
        rect.height = startRect.height + minMove / canvaZoom;
        if (rect.y > startRect.y + startRect.height - 2)
            rect.y = startRect.y + startRect.height - 2;
        if (rect.height < 2)
            rect.height = 2;
    }
    if ([SelectedItemActionsEnum.ResizeBottom].includes(resizeType)) {
        let minMove = e.clientY - mouseStartResizeCoords.y;
        rect.x = startRect.x - minMove / 2 / canvaZoom;
        rect.width = startRect.width + minMove / canvaZoom;
        if (rect.x > startRect.x + startRect.width - 2)
            rect.x = startRect.x + startRect.width - 2;
        if (rect.width < 2)
            rect.width = 2;
        rect.height = startRect.height + minMove / canvaZoom;
        if (rect.y > startRect.y + startRect.height - 2)
            rect.y = startRect.y + startRect.height - 2;
        if (rect.height < 2)
            rect.height = 2;
    }
    if ([SelectedItemActionsEnum.ResizeTop].includes(resizeType)) {
        let minMove = e.clientY - mouseStartResizeCoords.y;
        rect.x = startRect.x + minMove / 2 / canvaZoom;
        rect.width = startRect.width - minMove / canvaZoom;
        if (rect.x > startRect.x + startRect.width / 2 - 2)
            rect.x = startRect.x + startRect.width / 2 - 2;
        if (rect.width < 2)
            rect.width = 2;
        rect.y = startRect.y + minMove / canvaZoom;
        rect.height = startRect.height - minMove / canvaZoom;
        if (rect.y > startRect.y + startRect.height - 2)
            rect.y = startRect.y + startRect.height - 2;
        if (rect.height < 2)
            rect.height = 2;
    }
    if ([SelectedItemActionsEnum.ResizeLeft].includes(resizeType)) {
        let minMove = e.clientX - mouseStartResizeCoords.x;
        rect.x = startRect.x + minMove / canvaZoom;
        rect.width = startRect.width - (minMove) / canvaZoom;
        if (rect.x > startRect.x + startRect.width - 2)
            rect.x = startRect.x + startRect.width - 2;
        if (rect.width < 2)
            rect.width = 2;
        rect.y = startRect.y + (minMove / 2) / canvaZoom;
        rect.height = startRect.height - (minMove) / canvaZoom;
        if (rect.y > startRect.y + startRect.height / 2 - 2)
            rect.y = startRect.y + startRect.height / 2 - 2;
        if (rect.height < 2)
            rect.height = 2;
    }
    if ([SelectedItemActionsEnum.ResizeRight].includes(resizeType)) {
        let minMove = e.clientX - mouseStartResizeCoords.x;
        rect.width = startRect.width + (minMove) / canvaZoom;
        if (rect.x > startRect.x + startRect.width - 2)
            rect.x = startRect.x + startRect.width - 2;
        if (rect.width < 2)
            rect.width = 2;
        rect.y = startRect.y - (minMove / 2) / canvaZoom;
        rect.height = startRect.height + (minMove) / canvaZoom;
        if (rect.y > startRect.y + startRect.height / 2 - 2)
            rect.y = startRect.y + startRect.height / 2 - 2;
        if (rect.height < 2)
            rect.height = 2;
    }
    return rect;
}
